<template>
  <div>
    <div>
      <h3>Claims Dashboard</h3>
      <p>All claims across all orders & carriers</p>
    </div>
    <!-- KPI Cards -->
    <div class="row mb-4">
      <div class="col-sm-6 col-xl-3 mb-3">
        <div class="card h-100">
          <div class="card-body">
            <div class="text-muted small mb-2 text-uppercase">Open Claims</div>
            <h3 class="mb-2 red-text">{{ kpi.openCount }}</h3>
            <small v-if="kpi.weekDiff !== null" class="text-muted">
              <i :class="kpi.weekDiff > 0 ? 'fas fa-arrow-up' : kpi.weekDiff < 0 ? 'fas fa-arrow-down' : 'fas fa-minus'"></i>
              {{ Math.abs(kpi.weekDiff) }} vs last week
            </small>
          </div>
        </div>

      </div>
      <div class="col-sm-6 col-xl-3 mb-3">
        <div class="card h-100">
          <div class="card-body">
            <div class="text-muted small mb-2 text-uppercase">Total Exposure</div>
            <h3 class="mb-2 yellow-text">{{ formatDollars(kpi.totalExposure) }}</h3>
            <small class="text-muted">
              Across open claims
            </small>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl-3 mb-3">
        <div class="card h-100">
          <div class="card-body">
            <div class="text-muted small mb-2 text-uppercase">Within SLA</div>
            <h3 class="mb-2 green-text">{{ kpi.slaPercent !== null ? kpi.slaPercent + '%' : '—' }}</h3>
            <small v-if="kpi.slaOnTrack !== null" class="text-muted">{{ kpi.slaOnTrack }} of {{ kpi.slaTotal }} on track</small>
          </div>
        </div>
      </div>

      <div class="col-sm-6 col-xl-3 mb-3">
        <div class="card h-100">
          <div class="card-body">
            <div class="text-muted small mb-2 text-uppercase">Avg Resolution</div>
            <h3 class="mb-2 blue-text">{{ kpi.avgResolutionDays !== null ? kpi.avgResolutionDays + ' days' : '—' }}</h3>
            <small class="text-muted">
              Last 30 days
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters + Actions -->
    <div class="d-flex align-items-center flex-wrap mb-3" style="gap: 16px;">
      <div class="d-flex align-items-center claims-filter-wrap" style="gap: 8px; min-width: 320px;">
        <label class="mb-0 font-weight-semibold text-nowrap">Status:</label>
        <ValidatedMultiselect
          id="claims-status-filter"
          :value="filters.status"
          :options="statusFilterOptions"
          option-label-key="label"
          placeholder="All statuses"
          :multiple="true"
          :allow-empty="true"
          @input="onStatusChange"
        />
      </div>
      <div class="d-flex align-items-center" style="gap: 8px; min-width: 220px;">
        <label class="mb-0 font-weight-semibold text-nowrap">Carrier:</label>
        <BaseSelect
          id="claims-carrier-filter"
          v-model="filters.carrierId"
          :options="carrierOptions"
          default-text="All carriers"
          @input="resetAndFetch"
        />
      </div>
      <div class="d-flex align-items-center" style="gap: 8px; min-width: 220px;">
        <label class="mb-0 font-weight-semibold text-nowrap">Assigned:</label>
        <BaseSelect
          id="claims-assigned-filter"
          v-model="filters.assignedUserId"
          :options="assignedUserOptions"
          default-text="All agents"
          @input="resetAndFetch"
        />
      </div>
      <div class="ml-auto d-flex" style="gap: 8px;">
        <b-button variant="outline-secondary" size="sm" @click="exportCsv">
          <i class="fas fa-download mr-1"></i> Export CSV
        </b-button>
        <b-button variant="success" size="sm" @click="$bvModal.show('new-claim-modal')">
          <i class="fas fa-plus mr-1"></i> New Claim
        </b-button>
      </div>
    </div>

    <div class="d-flex flex-row justify-content-center" style="gap: 8px;">
    <div class="d-flex align-items-center justify-content-center mb-2" style="gap: 4px;">
      <div class="legend green-bg"></div>
      <div>Opened</div>
    </div>
    <div class="d-flex align-items-center justify-content-center mb-2" style="gap: 4px;">
      <div class="legend red-bg"></div>
      <div>SLA Overdue</div>
    </div>
    <div class="d-flex align-items-center justify-content-center mb-2" style="gap: 4px;">
      <i class="fas fa-check-circle green-text"></i>
      <div>Closed</div>
    </div>
    </div>

    <!-- Table -->
    <b-card no-body>
      <!-- Top pagination -->
      <div class="px-3 pt-3">
        <PageLinks
          :current-page="currentPage"
          :has-next-page="hasNextPage"
          :on-page-change="updatePageUrlParam"
          :is-disabled="loading"
        />
      </div>

      <hr class="border-light mt-3 mb-0" />

      <LoadingIndicator v-if="loading" centered />

      <div v-else-if="!claims.length" class="px-4 py-3 text-muted">No claims found</div>

      <table v-else class="table claims-table mb-0">
        <thead>
          <tr>
            <TableSortableHeader label="Claim ID" field="id" />
            <TableSortableHeader label="Order ID" field="lead_id" />
            <TableSortableHeader label="Carrier" field="carrier_id" />
            <TableSortableHeader label="Agent" field="assigned_user_id" />
            <TableSortableHeader label="Category" field="category" />
            <TableSortableHeader label="Amount" field="amount_to_recover" />
            <TableSortableHeader label="Severity" field="severity" />
            <TableSortableHeader label="Status" field="status" />
            <th>SLA</th>
            <TableSortableHeader label="Open date" field="opened_at" />
            <!--<TableSortableHeader label="Close date" field="closed_at" />-->
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="claim in claims"
            :key="claim.id"
            :class="{
              'gray-bg': claim.status === 'closed_paid' || claim.status === 'closed_denied',
              'red-bg': (claim.status !== 'closed_paid' && claim.status !== 'closed_denied') && slaDaysRemaining(claim) !== null && slaDaysRemaining(claim) < 0,
              'green-bg': (claim.status !== 'closed_paid' && claim.status !== 'closed_denied') && (slaDaysRemaining(claim) === null || slaDaysRemaining(claim) >= 0)
            }"
          >
            <td>
              <router-link v-if="claim.id && claim.lead" :to="{ name: 'OrderClaims', params: { id: claim.lead.id }, query: { openClaimId: claim.id } }" @click.native.stop>
                <strong class="blue-text">CLM-{{ String(claim.id).padStart(4, '0') }}</strong>
              </router-link>
              <strong v-else-if="claim.id">CLM-{{ String(claim.id).padStart(4, '0') }}</strong>
            </td>
            <td>
              <router-link v-if="claim.lead" :to="{ name: 'OrderDetails', params: { id: claim.lead.id } }" @click.native.stop>
                #{{ claim.lead.id }}
              </router-link>
              <span v-else>—</span>
            </td>
            <td>
              <router-link v-if="claim.carrier && claim.carrier.contact" :to="{ name: 'CarrierDetails', params: { id: claim.carrier.id } }" @click.native.stop>
                {{ claim.carrier.contact.company }}
              </router-link>
              <span v-else>—</span>
            </td>
            <td>
              <router-link v-if="claim.assignedUser" :to="{ name: 'UserDetails', params: { id: claim.assignedUser.id } }" @click.native.stop>
                {{ claim.assignedUser.username }}
              </router-link>
              <span v-else>—</span>
            </td>
            <td>{{ categoryLabel(claim.category) }}</td>
            <td>{{ claim.amountToRecover ? formatDollars(claim.amountToRecover) : '—' }}</td>
            <td><span :class="['claimBadge', severityBadgeClass(claim.severity)]">{{ capitalize(claim.severity) }}</span></td>
            <td><span :class="['claimBadge', statusBadgeClass(claim.status)]">{{ statusLabel(claim.status) }}</span></td>
            <td>
              <span v-if="claim.status !== 'closed_paid' && claim.status !== 'closed_denied'" :class="slaClass(claim)" :title="slaDueDateStr(claim)">{{ slaLabel(claim) }}</span>
              <span v-else>—</span>
            </td>
            <td class="d-flex align-items-center" style="gap: 6px;">
              {{ claim.openedAt | formatDate }}
              <i v-if="claim.closedAt" class="fas fa-check-circle green-text" :title="'Closed: ' + $options.filters.formatDate(claim.closedAt)"></i>
            </td>
           <!--<td><span v-if="claim.closedAt">{{ claim.closedAt | formatDate }}</span><span v-else>—</span></td>-->
            <td>
              <b-button size="sm" variant="outline-danger" class="claim-delete-btn" @click.stop="deleteClaim(claim)">
                <i class="far fa-trash-alt"></i>
              </b-button>
            </td>
          </tr>
        </tbody> 
      </table>

      <!-- Bottom pagination --> 
      <div class="px-3 py-3">
        <PageLinks
          :current-page="currentPage" 
          :has-next-page="hasNextPage"
          :on-page-change="updatePageUrlParam"
          :is-disabled="loading" 
        />

      </div> 
    </b-card>
    <b-modal
      id="new-claim-modal"
      title="New Claim — Enter Order ID"
      ok-title="Continue"
      centered
      :ok-disabled="!newClaimOrderId"
      @ok.prevent="goToNewClaim"
      @hidden="newClaimOrderId = ''"
    >
      <ValidatedInput
        id="new-claim-order-id"
        v-model="newClaimOrderId"
        label="Order ID"
        @keyup.enter="goToNewClaim"
      />
    </b-modal>
  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import pageLinksMixin from '@/mixins/pageLinks';
import BaseSelect from '@/components/shared/BaseSelect';
import LoadingIndicator from '@/components/shared/LoadingIndicator';
import TableSortableHeader from '@/components/shared/TableSortableHeader';
import ValidatedInput from '@/components/shared/ValidatedInput';
import ValidatedMultiselect from '@/components/shared/ValidatedMultiselect';
import { claimCategoryLabels, claimStatusOptions, claimSeverityOptions } from '@/helpers/claimOptions';

const isClosed = c => c.status === 'closed_paid' || c.status === 'closed_denied';

export default {
  name: 'Claims',
  metaInfo: { title: 'Claims' },
  components: { BaseSelect, LoadingIndicator, TableSortableHeader, ValidatedInput, ValidatedMultiselect },
  mixins: [pageLinksMixin],

  data() {
    return {
      loading: false,
      claims: [],
      filters: {
        status: [],
        carrierId: null,
        assignedUserId: null,
      },
      statusFilterOptions: claimStatusOptions,
      carrierOptions: [{ value: null, title: 'All carriers' }],
      assignedUserOptions: [{ value: null, title: 'All agents' }],
      newClaimOrderId: '',
      kpi: {
        openCount: 0,
        totalExposure: 0,
        slaPercent: null,
        slaOnTrack: null,
        slaTotal: null,
        avgResolutionDays: null,
        weekDiff: null,
      },
      refreshTimer: null,
    };
  },

  computed: {
    hasNextPage() {
      return this.claims.length === this.perPage;
    },
  },

  watch: {
    '$route.query'() {
      this.fetch();
    },
  },

  created() {
    this.fetch();
  },

  async mounted() {
    await this.fetchKpi();
    this.refreshTimer = setInterval(() => { this.fetchKpi(); this.fetch(); }, 60000);
  },

  beforeDestroy() {
    clearInterval(this.refreshTimer);
  },

  methods: {
    async fetch() {
      if (this.loading) return;
      this.loading = true;
      try {
        const baseParams = { page: this.currentPage, per_page: this.perPage };
        if (this.$route.query.sort) baseParams.sort = this.$route.query.sort;
        if (this.filters.carrierId)      baseParams.carrier_id = this.filters.carrierId;
        if (this.filters.assignedUserId) baseParams.assigned_user_id = this.filters.assignedUserId;

        if (this.filters.status && this.filters.status.length) {
          baseParams.status = this.filters.status.join(',');
        }
        const res = await ApiClient.claims.get({ params: baseParams });
        if (res.status === 200) {
          this.claims = res.data.data;
        }
      } finally {
        this.loading = false;
      }
    },

    async fetchKpi() {
      const res = await ApiClient.claims.get({ params: { per_page: 150 } });
      if (res.status !== 200) return;

      const all = res.data.data;
      const open = all.filter(c => !isClosed(c));
      const closed = all.filter(isClosed);
      const msNow = Date.now();
      const now = new Date();

      this.kpi.openCount = open.length;

      const thisWeek = open.filter(c => c.openedAt && msNow - new Date(c.openedAt) <= 7 * 86400000);
      const prevWeek = all.filter(c => {
        if (!c.openedAt) return false;
        const age = msNow - new Date(c.openedAt);
        return age > 7 * 86400000 && age <= 14 * 86400000;
      });
      this.kpi.weekDiff = thisWeek.length - prevWeek.length;

      this.kpi.totalExposure = open.reduce((sum, c) =>
        sum + (parseInt(c.amountToRecover) || 0) - (parseInt(c.amountRecovered) || 0), 0);

      const slaTracked = open.filter(c => this.slaDueDate(c) !== null);
      const slaOnTrack = slaTracked.filter(c => this.slaDueDate(c) >= now);
      this.kpi.slaOnTrack = slaOnTrack.length;
      this.kpi.slaTotal = slaTracked.length;
      this.kpi.slaPercent = slaTracked.length > 0
        ? Math.round((slaOnTrack.length / slaTracked.length) * 100)
        : null;

      const seenCarriers = new Set();
      const carriers = [{ value: null, title: 'All carriers' }];
      for (const c of all) {
        if (c.carrier && c.carrier.id && !seenCarriers.has(c.carrier.id)) {
          seenCarriers.add(c.carrier.id);
          carriers.push({
            value: c.carrier.id,
            title: c.carrier.contact ? c.carrier.contact.company : `Carrier #${c.carrier.id}`,
          });
        }
      }
      this.carrierOptions = carriers;

      const seenUsers = new Set();
      const agents = [{ value: null, title: 'All agents' }];
      for (const c of all) {
        if (c.assignedUser && c.assignedUser.id && !seenUsers.has(c.assignedUser.id)) {
          seenUsers.add(c.assignedUser.id);
          agents.push({ value: c.assignedUser.id, title: c.assignedUser.username });
        }
      }
      this.assignedUserOptions = agents;

      if (closed.length) {
        const totalDays = closed.reduce((sum, c) =>
          sum + Math.ceil((new Date(c.closedAt) - new Date(c.openedAt)) / 86400000), 0);
        this.kpi.avgResolutionDays = Math.round(totalDays / closed.length);
      }
    },

    onStatusChange(values) {
      if (JSON.stringify(values) !== JSON.stringify(this.filters.status)) {
        this.filters.status = values;
        this.resetAndFetch();
      }
    },

    resetAndFetch() {
      if (this.currentPage !== 1) {
        this.updatePageUrlParam(1);
      } else {
        this.fetch();
      }
    },

    slaDueDate(claim) {
      if (!claim.openedAt || !claim.slaDays) return null;
      return new Date(new Date(claim.openedAt).getTime() + claim.slaDays * 86400000);
    },

    slaDaysRemaining(claim) {
      const due = this.slaDueDate(claim);
      if (!due) return null;
      return Math.ceil((due - new Date()) / 86400000);
    },

    slaDueDateStr(claim) {
      const due = this.slaDueDate(claim);
      return due ? 'Due: ' + due.toLocaleDateString('en-US') : '';
    },

    slaLabel(claim) {
      const days = this.slaDaysRemaining(claim);
      if (days === null) return '—';
      if (days < 0) return 'OD ' + Math.abs(days) + 'd';
      if (days === 0) return 'Today';
      return days + 'd';
    },

    slaClass(claim) {
      const days = this.slaDaysRemaining(claim);
      if (days === null) return 'text-muted';
      if (days < 0) return 'font-weight-bold red-text red-text-dark';
      if (days <= 3) return 'font-weight-bold orange-text';
      return 'green-text';
    },

    statusLabel(s) { return (claimStatusOptions.find(o => o.value === s) || {}).label || s || ''; },
    statusBadgeClass(s) { return (claimStatusOptions.find(o => o.value === s) || {}).badge || ''; },
    severityBadgeClass(s) {
      return (claimSeverityOptions.find(o => o.value === s) || {}).badge || '';
    },
    capitalize(val) { return val ? val.charAt(0).toUpperCase() + val.slice(1) : '—'; },
    categoryLabel(val) { return claimCategoryLabels[val] || val || '—'; },

    formatDollars(val) {
      const n = parseFloat(val);
      if (isNaN(n)) return '—';
      return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    goToNewClaim() {
      if (!this.newClaimOrderId) return;
      this.$bvModal.hide('new-claim-modal');
      this.$router.push({ name: 'OrderClaimNew', params: { id: String(this.newClaimOrderId) } });
    },

    async deleteClaim(claim) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Delete CLM-${String(claim.id).padStart(4, '0')}? This action cannot be undone.`,
        { title: 'Delete Claim', okVariant: 'danger', okTitle: 'Delete', centered: true }
      );
      if (!confirmed) return;
      const res = await ApiClient.claims.delete({ id: claim.id });
      if (res.status === 200 || res.status === 204) {
        this.fetch();
      }
    },

    exportCsv() {
      const headers = ['Claim ID', 'Order ID', 'Carrier', 'Agent', 'Category', 'Amount', 'Severity', 'Status', 'SLA', 'Open date', 'Close date'];
      const rows = this.claims.map(c => [
        `CLM-${String(c.id).padStart(4, '0')}`,
        c.lead ? c.lead.id : '',
        c.carrier && c.carrier.contact ? c.carrier.contact.company : '',
        c.assignedUser ? c.assignedUser.username : '',
        this.categoryLabel(c.category),
        c.amountToRecover || '',
        this.capitalize(c.severity),
        this.statusLabel(c.status),
        !isClosed(c) ? this.slaLabel(c) : '',
        c.openedAt ? new Date(c.openedAt).toLocaleDateString('en-US') : '',
        c.closedAt ? new Date(c.closedAt).toLocaleDateString('en-US') : '',
      ]);
      const csv = [headers, ...rows]
        .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `claims-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
  },
};
</script>
<style>
.redBadge { background-color: #F5DEE8; color: #C31813; border-color: #F1BABC; }
.dark-style .redBadge { background-color: #4F3748; border-color:#A34B48; color: #F86F6B;}
.orangeBadge { background-color: #F5E6E2; color: #9A3413; border-color: #F1CFBA; }
.dark-style .orangeBadge { background-color: #453D46; color: #FDBA74; border-color:#93602B; }
.yellowBadge { background-color: #FBF2BF; color: #885118; border-color: #E7D3B4;}
.dark-style .yellowBadge { background-color: #3D3618; color: #FFDF6D; border-color: #E7D3B4;}
.greenBadge { background-color: #DDECEA; color: #008E5A; border-color: #B7D6BF;}
.dark-style .greenBadge { background-color: #224332; color: #95D596; border-color: #308767;}
.grayBadge { background-color: #E1E3EF; color: #334155; border-color: #C9CBDB; }
.dark-style .grayBadge { background-color: #353F56; color: #CAD5E0; border-color:#758391; }
.blueBadge { background-color: #DFECFD; color: #0B8AFF; border-color: #B9CFEC; }
.dark-style .blueBadge { background-color: #2B446E; color: #44B1FF; border-color: #1A78BC; }
.purpleBadge { background-color: #EADFFD; color: #6B21A7; border-color: #D7CCEA; }
.dark-style .purpleBadge { background-color: #3D2763; color: #C084FC; border-color: #9A6CCD; }

.claimBadge {
  display: inline-block;
  padding: 2px 16px;
  border-radius: 100px;
  font-size: 0.78rem;
  font-weight: 500;
  border-width: 1px;
  border-style: solid;
}

.red-bg { background-color: #FFF5EF; }
.dark-style .red-bg { background-color: #B1615E; }
.dark-style .claims-table .red-bg { background-color: #563A3D; }
.green-bg { background-color: #F1FFF5; }
.dark-style .green-bg { background-color: #486D7B; }
.dark-style .red-text-dark { color: #4F3748; }
.dark-style .claims-table .red-text-dark { color: #FF8A87; }
.dark-style .claim-delete-btn {
  color: #CAD5E0;
  border-color: #758391;
}
.dark-style .claim-delete-btn:hover {
  color: #FFFFFF;
  background-color: #758391;
  border-color: #758391;
}
.legend {
  width: 24px;
  height: 24px;
  padding: 3px;
}
.claims-filter-wrap .form-group {
  margin-bottom: 0;
}
.claims-filter-wrap .multiselect__content-wrapper {
  width: 100% !important;
  min-width: 100% !important;
}
</style>
